import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, HardDriveUpload, RotateCcw, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "./ui";

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 3600000;
  if (diff < 1) return `${Math.round(diff * 60)}m ago`;
  if (diff < 24) return `${Math.round(diff)}h ago`;
  return `${Math.round(diff / 24)}d ago`;
}

function isOverdue(w) {
  const hours = (Date.now() - new Date(w.last_backup).getTime()) / 3600000;
  return hours > (w.backup_frequency_hours || 24);
}

export default function Workloads() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "Server", os: "", location: "On-Prem" });
  const [detail, setDetail] = useState(null);
  const [points, setPoints] = useState([]);
  const [policy, setPolicy] = useState({ backup_frequency_hours: 24, retention_days: 30 });
  const [busy, setBusy] = useState("");

  const load = () => api.get("/workloads").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const addWorkload = async () => {
    try {
      await api.post("/workloads", form);
      toast.success("Workload added & protected");
      setOpen(false);
      setForm({ name: "", type: "Server", os: "", location: "On-Prem" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runBackup = async (id) => {
    setBusy(id);
    try {
      await api.post(`/workloads/${id}/backup`);
      toast.success("Restore point created & verified");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const openDetail = async (w) => {
    setDetail(w);
    setPolicy({ backup_frequency_hours: w.backup_frequency_hours || 24, retention_days: w.retention_days || 30 });
    const { data } = await api.get(`/workloads/${w.id}/restore-points`);
    setPoints(data);
  };

  const savePolicy = async () => {
    try {
      await api.put(`/workloads/${detail.id}/policy`, {
        backup_frequency_hours: Number(policy.backup_frequency_hours),
        retention_days: Number(policy.retention_days),
      });
      toast.success("Backup policy updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const restore = async (rid) => {
    try {
      const { data } = await api.post(`/restore-points/${rid}/restore`);
      toast.success(data.message);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="BACKUP & RECOVERY" title="Protected Workloads" subtitle="Immutable restore points across on-prem and multi-cloud">
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-workload-btn" className="rounded-none bg-primary hover:bg-primary/90 text-white"><Plus className="h-4 w-4 mr-1" /> Add Workload</Button>
            </DialogTrigger>
            <DialogContent className="bg-background border-border rounded-none">
              <DialogHeader><DialogTitle className="font-heading tracking-tight">Add Protected Workload</DialogTitle><DialogDescription className="text-xs text-muted-foreground">Register an asset for immutable backup protection.</DialogDescription></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label className="text-xs text-muted-foreground">Name</Label><Input data-testid="workload-name" className="rounded-none bg-secondary/40 border-border mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-muted-foreground">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger className="rounded-none bg-secondary/40 border-border mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{["Server", "Database", "Workstation", "File Server", "Virtual Machine"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs text-muted-foreground">Location</Label>
                    <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                      <SelectTrigger className="rounded-none bg-secondary/40 border-border mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{["On-Prem", "AWS eu-central-1", "Azure West-EU", "GCP europe-west3"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-xs text-muted-foreground">OS / Platform</Label><Input data-testid="workload-os" className="rounded-none bg-secondary/40 border-border mt-1.5" value={form.os} onChange={(e) => setForm({ ...form, os: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="workload-save" onClick={addWorkload} className="rounded-none bg-primary hover:bg-primary/90 text-white">Add & Protect</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="p-8">
        <div className="card-elevated rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="workloads-table">
            <thead>
              <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-3">Workload</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last Backup</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Policy</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.id} className="border-b border-border last:border-0 hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3">
                    <button onClick={() => openDetail(w)} className="flex items-center gap-2 hover:text-primary transition-colors font-mono text-xs" data-testid={`workload-row-${w.id}`}>
                      <Server className="h-3.5 w-3.5" /> {w.name}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{w.type}</td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{w.location}</td>
                  <td className="px-4 py-3"><StatusBadge value={w.status} /></td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{timeAgo(w.last_backup)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{w.backup_size_gb} GB</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    <span className="text-muted-foreground">every {w.backup_frequency_hours || 24}h</span>
                    {isOverdue(w) && <span className="ml-2 text-primary">OVERDUE</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && (
                      <Button size="sm" variant="ghost" disabled={busy === w.id} onClick={() => runBackup(w.id)} data-testid={`backup-btn-${w.id}`} className="rounded-none text-xs hover:text-[hsl(var(--chart-1))]">
                        <HardDriveUpload className="h-3.5 w-3.5 mr-1" /> {busy === w.id ? "Running…" : "Backup"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-background border-border rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{detail?.name} · Restore Points</DialogTitle><DialogDescription className="text-xs text-muted-foreground">Verified recovery points for this workload.</DialogDescription></DialogHeader>
          {isAdmin && (
            <div className="border border-border p-4 mb-2" data-testid="policy-editor">
              <p className="font-mono text-[10px] tracking-widest text-muted-foreground mb-3">BACKUP POLICY · AUTOMATED SCHEDULE</p>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">Frequency (hours)</Label>
                  <Input data-testid="policy-frequency" type="number" min="1" className="rounded-none bg-secondary/40 border-border mt-1.5" value={policy.backup_frequency_hours} onChange={(e) => setPolicy({ ...policy, backup_frequency_hours: e.target.value })} />
                </div>
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">Retention (days)</Label>
                  <Input data-testid="policy-retention" type="number" min="1" className="rounded-none bg-secondary/40 border-border mt-1.5" value={policy.retention_days} onChange={(e) => setPolicy({ ...policy, retention_days: e.target.value })} />
                </div>
                <Button data-testid="policy-save" onClick={savePolicy} className="rounded-none bg-primary hover:bg-primary/90 text-white">Save</Button>
              </div>
            </div>
          )}
          <div className="space-y-2 max-h-96 overflow-auto">
            {points.map((p) => (
              <div key={p.id} className="border border-border p-3 flex items-center justify-between">
                <div>
                  <p className="font-mono text-xs">{new Date(p.created_at).toLocaleString()}</p>
                  <p className="font-mono text-[10px] text-muted-foreground mt-1">{p.type} · {p.size_gb} GB · sha256:{p.checksum.slice(0, 12)}…</p>
                </div>
                {isAdmin && (
                  <Button size="sm" variant="outline" className="rounded-none border-border text-xs" onClick={() => restore(p.id)} data-testid={`restore-btn-${p.id}`}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                  </Button>
                )}
              </div>
            ))}
            {points.length === 0 && <p className="text-sm text-muted-foreground">No restore points yet.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
